import { faCircleNotch, faClose, faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ActionDialog from "components/reusable/ActionDialog";
import BottomSheet from "components/reusable/BottomSheet";
import ErrorMessage from "components/reusable/ErrorMessage";
import OtpDialog from "components/reusable/OtpDialog";
import AuthContext from "context/AuthContext";
import { Button } from "flowbite-react";
import useToastContext from "hooks/useToastContext";
import { useContext, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { apiDelete, apiPost } from "utils/apiFetch";
import pathObj from "utils/apiPath";

const EditPhoneNumber = ({ onClose }) => {
  const { t } = useTranslation();
  const { user, getProfile, storeTokens } = useContext(AuthContext);
  const notification = useToastContext();
  const {
    register,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm({ mode: "onChange", shouldFocusError: true, defaultValues: {} });

  const [loading, setLoading] = useState(false);
  const [showWarningDialogPhone, setShowWarningDialogPhone] = useState(false);
  const [showWarningDeleteDialogPhone, setShowWarningDeleteDialogPhone] = useState(false);

  const [verifySecondaryMobileBody, setVerifySecondaryMobileBody] = useState({});
  const [showVerifySecondaryPhoneOtp, setShowVerifySecondaryPhoneOtp] = useState(false);

  const changePrimaryMobile = async () => {
    setLoading(true);
    try {
      const res = await apiPost(pathObj.changePrimaryMobile, {});
      if (res.data.success) {
        notification.success(res.data.message);
        getProfile();
        onClose();
      } else {
        notification.error(res.data.message);
      }
    } catch (err) {}
    setLoading(false);
  };

  const deleteSecondaryMobile = async () => {
    setLoading(true);
    try {
      const res = await apiDelete(pathObj.deleteSecondaryMobile);
      if (res.data.success) {
        notification.success(res.data.message);
        getProfile();
        onClose();
      }
    } catch (err) {}
    setLoading(false);
  };

  const preventSpace = (e) => {
    e.target.value = e.target.value.trim();
  };

  const onSecondaryPhoneAdd = async (data) => {
    setLoading(true);
    try {
      var body = {
        secondaryCountryCode: data.secondary_country_code,
        secondaryMobile: data.secondary_phone,
      };
      setVerifySecondaryMobileBody(body);
      const res = await apiPost(pathObj.updateSecondaryMobile, body);
      if (res.data.success) {
        notification.success(res.data.message);
        setShowVerifySecondaryPhoneOtp(true);
      } else {
        notification.error(res.data.message);
      }
    } catch (err) {}
    setLoading(false);
  };

  return (
    <>
      <BottomSheet
        open={true}
        onClose={() => {
          onClose();
        }}
        title={t("SET_PRIMARY_PHONE_NUMBER")}
      >
        <div className="font-medium pb-5">{t("SET_PRIMARY_PHONE_NUMBER_WARNING")}</div>
        <div className="flex flex-col justify-between">
          <div className="flex gap-1">
            <input type="checkbox" checked onClick={() => false} />
            <span>
              +{user.country_code} {user.mobile}
            </span>
          </div>

          {user.secondaryMobile ? (
            <>
              <div className="flex justify-between items-center gap-1">
                <div className="flex gap-1">
                  <input
                    type="checkbox"
                    onClick={(e) => {
                      if (e.target.checked) {
                        setShowWarningDialogPhone(true);
                      }
                    }}
                  />
                  <span>
                    +{user.secondaryCountryCode} {user.secondaryMobile}
                  </span>
                </div>
                <FontAwesomeIcon
                  icon={faClose}
                  className="text-gray-600 cursor-pointer cursor-pointer"
                  onClick={() => {
                    setShowWarningDeleteDialogPhone(true);
                  }}
                />
              </div>
            </>
          ) : (
            <>
              <form onSubmit={handleSubmit(onSecondaryPhoneAdd)}>
                <div className="pt-5 pb-2 font-medium">{t("ADD_NUMBER")}</div>
                <div className="flex justify-between items-center">
                  <div className="col-span-2 flex gap-2">
                    <div className="w-[6rem]">
                      <input
                        type="text"
                        id="secondary_country_code"
                        className="text-gray-600 disabled inputform text-heading text-sm rounded-base block w-full px-3 py-2.5 shadow-xs placeholder:text-body"
                        placeholder={t("O_LAST_NAME")}
                        disabled
                        value="+355"
                        onKeyDown={(e) => preventSpace(e)}
                        required
                        {...register("secondary_country_code", {})}
                      />
                    </div>
                    <div className="w-full">
                      <input
                        type="number"
                        id="mobile"
                        inputType="numeric"
                        maxLength={9}
                        disabled={loading}
                        className="inputform text-heading text-sm rounded-base block w-full px-3 py-2.5 shadow-xs placeholder:text-body"
                        placeholder={t("O_MOBILE_NUMBER")}
                        onKeyDown={(e) => preventSpace(e)}
                        {...register("secondary_phone", {
                          required: t("REGISTER_MOBILE"),
                          min: {
                            value: 0,
                            message: t("REGISTER_MIN_VALUE"),
                          },
                          minLength: {
                            value: 9,
                            message: t("REGISTER_MIN_LENGTH_MOBILE"),
                          },
                        })}
                        required
                      />
                      <ErrorMessage message={errors?.secondary_phone?.message} />
                    </div>
                  </div>
                  {loading ? (
                    <FontAwesomeIcon icon={faCircleNotch} className="animate-spin text-gray-600" />
                  ) : (
                    <Button type="submit">
                      <FontAwesomeIcon icon={faPlus} className="text-gray-600 cursor-pointer cursor-pointer" />
                    </Button>
                  )}
                </div>
              </form>
            </>
          )}
        </div>
      </BottomSheet>
      {showWarningDialogPhone && (
        <ActionDialog
          open={showWarningDialogPhone}
          title={t("WARNING")}
          onPositive={() => {
            changePrimaryMobile();
            setShowWarningDialogPhone(false);
          }}
          onNegative={() => {
            setShowWarningDialogPhone(false);
          }}
          positiveText={t("CONFIRM")}
          negativeText={t("CANCEL")}
        >
          {t("SET_PRIMARY_PHONE_NUMBER_CONFIRMATION")}
        </ActionDialog>
      )}
      {showWarningDeleteDialogPhone && (
        <ActionDialog
          open={showWarningDeleteDialogPhone}
          title={t("WARNING")}
          onPositive={() => {
            deleteSecondaryMobile();
            setShowWarningDeleteDialogPhone(false);
          }}
          onNegative={() => {
            setShowWarningDeleteDialogPhone(false);
          }}
          positiveText={t("CONFIRM")}
          negativeText={t("CANCEL")}
        >
          {t("CONFIRM_DELETE_SECONDARY_PHONE_NUMBER")}
        </ActionDialog>
      )}
      {showVerifySecondaryPhoneOtp && (
        <OtpDialog
          otpURL={pathObj.updateSecondaryMobile}
          otpConfirmURL={pathObj.verifySecondaryMobile}
          body={verifySecondaryMobileBody}
          onSuccess={(message) => {
            notification.success(message || t("OPERATION_SUCCESS"));
            getProfile();
            onClose();
          }}
          onClose={() => {
            onClose();
          }}
        />
      )}
    </>
  );
};

export default EditPhoneNumber;
